// Per-person PINs for the AI phone line (docs/features/ai_phone_personal_pins_FEATURE.md). A teammate sets their own
// six-digit PIN from the user menu while signed in; after calling the AI line from their own phone they key it, and
// the AI knows it is them. The PIN goes to fleetWalkieControl, which keeps only a slow salted hash; it is never shown
// again, and nothing here stores it.
import { ref } from 'vue'
import { httpsCallable } from 'firebase/functions'
import { functions } from '@/services/firebaseconfig'

/** What a PIN the server refused needs to change (phonePins.js weakness). */
export const PIN_WEAKNESS = {
  'six-digits': 'Use exactly six digits.',
  'one-digit': 'Use more than one digit.',
  run: 'Avoid straight runs like 123456 or 987654.',
  repeat: 'Avoid repeated patterns like 121212 or 123123.',
  'own-phone': 'Do not use the last six digits of your own phone number.',
}

const REFUSALS = {
  'not-while-impersonating': 'A PIN cannot be changed while impersonating someone.',
  'not-in-a-test-session': 'A test session cannot change a PIN.',
  'not-authorized': 'Your account cannot use the AI phone line.',
  'invalid-audience': 'That account cannot use the AI phone line.',
  'super-admin-required': 'Only a super admin can clear a PIN.',
}

async function pinControl(operation, args = {}) {
  try {
    const result = await httpsCallable(functions, 'fleetWalkieControl', { timeout: 15000 })({
      ...args,
      operation,
    })
    return result.data
  } catch (error) {
    throw new Error(
      REFUSALS[error.message] || 'The PIN service is unavailable. Please try again shortly.'
    )
  }
}

export function usePhonePin() {
  const status = ref(null) // { set, setAt } for the signed-in person
  const loading = ref(false)
  const saving = ref(false)
  const error = ref('')

  async function loadStatus() {
    loading.value = true
    error.value = ''
    try {
      status.value = await pinControl('pinStatus')
    } catch (e) {
      error.value = e.message
    } finally {
      loading.value = false
    }
  }

  /** Saves the signed-in person's PIN. Returns true when saved; otherwise `error` says why. */
  async function savePin(pin, confirmation) {
    error.value = ''
    if (!/^\d{6}$/.test(pin || '')) {
      error.value = PIN_WEAKNESS['six-digits']
      return false
    }
    if (pin !== confirmation) {
      error.value = 'The two PINs do not match.'
      return false
    }
    saving.value = true
    try {
      const result = await pinControl('setPin', { pin })
      if (!result?.ok) {
        error.value = PIN_WEAKNESS[result?.why] || 'That PIN cannot be used. Try another.'
        return false
      }
      status.value = { set: true, setAt: result.setAt }
      return true
    } catch (e) {
      error.value = e.message
      return false
    } finally {
      saving.value = false
    }
  }

  /** A super admin clears someone's PIN so they set a new one. Returns { cleared } (false when none was set). */
  async function clearPinFor(humanUid) {
    return pinControl('clearPin', { humanUid })
  }

  return { status, loading, saving, error, loadStatus, savePin, clearPinFor }
}
